import React from 'react';
import PropTypes from 'prop-types';

// The logo picker, drawn as a button + filename slot + clear icon instead of a bare
// <input type="file">. A native input can only ever name the file the user just
// picked, so a template that already had a logo showed "No file chosen" — the
// control said nothing about the thing removal would act on.
//
// The real input stays in the DOM, moved off-screen rather than display:none, so
// Capybara's attach_file can still reach it and the browser's own picker dialog
// still opens from the button.
//
// The slot shows exactly one name and the icon acts on whatever it shows. An
// unsaved pick wins over the saved logo, because the pick is what a save would
// actually submit. Deciding what clearing MEANS is the parent's business — this
// component only reports that it happened.
class LogoFileField extends React.Component {
  constructor(props) {
    super(props);
    this.inputRef = React.createRef();
    this.clearButtonRef = React.createRef();
    this.buttonRef = React.createRef();
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleClear = this.handleClear.bind(this);
  }

  // A staged removal disables the picker and swaps the ✗ for an inert span; once
  // it's cancelled the button re-mounts as the same DOM node type but a fresh
  // element, so nothing else would carry focus back to it.
  //
  // The two guards below can't both fire on one update: the first requires
  // removePending to have just been true, which only happens once a saved logo
  // exists (removal can't be staged over an empty picker); the second requires
  // `shown` to have just gone truthy -> falsy, which with a saved logo in play
  // never happens, since savedFilename is a stable prop for the life of this
  // component and removePending going false leaves it as the remaining truthy
  // source for `shown`.
  componentDidUpdate(prevProps) {
    if (prevProps.removePending && !this.props.removePending && this.clearButtonRef.current) {
      this.clearButtonRef.current.focus();
    }
    // The two moves are mutually exclusive, so neither needs to guard against the
    // other: cancelling a staged removal implies a saved logo (only a saved one can
    // be staged), and savedFilename is a server prop fixed for the mount — so the
    // slot is non-empty on both sides of that transition and the test below is false.
    //
    // Discarding the only pick on a template with no saved logo unmounts the ✗
    // with nothing to replace it (the field falls back to "No file chosen", not
    // a button), so focus would otherwise drop to <body> right as the user
    // finishes correcting a mis-pick -- the normal state of a new template.
    const hadShown = prevProps.pickedName || prevProps.savedFilename;
    const shown = this.props.pickedName || this.props.savedFilename;
    if (hadShown && !shown && this.buttonRef.current) {
      this.buttonRef.current.focus();
    }
  }

  handleButtonClick() {
    this.inputRef.current.click();
  }

  // Read files[0] before resetting the input's value below -- the reset is what
  // makes re-picking the SAME file work: the browser fires no change event when
  // the selection is unchanged, so a discarded pick could otherwise never be
  // re-made without choosing some other file first. Reading first is required
  // because the reset also clears the FileList this line needs to read.
  handleChange(e) {
    const file = e.target.files[0] || null;
    this.props.onPick(file);
    e.target.value = '';
  }

  handleClear() {
    this.props.onClear();
  }

  render() {
    const { savedFilename, pickedName, removePending } = this.props;
    const shown = pickedName || savedFilename;
    return (
      <div className="qr-logo-file">
        {/* No `name` — this never submits raw bytes. onPick lifts the picked File so
            QrStyleForm can upload it and submit the resulting signed id via a hidden
            field instead. */}
        <input
          type="file"
          accept="image/png"
          className="qr-logo-file__input"
          data-testid="logo-file"
          ref={this.inputRef}
          disabled={removePending}
          tabIndex={-1}
          aria-hidden="true"
          onChange={this.handleChange}
        />
        <button
          type="button"
          className="btn btn-default btn-sm qr-logo-file__button"
          data-testid="logo-file-button"
          disabled={removePending}
          ref={this.buttonRef}
          onClick={this.handleButtonClick}
        >
          {savedFilename ? 'Choose new file' : 'Choose file'}
        </button>
        {/* aria-live carries the news a discard leaves unspoken: the ✗ button's
            aria-label flips silently from "Discard the selected file" back to
            "Remove logo" once the pick is gone, but this text does not change
            when a removal is staged (only the strikethrough class does), so it
            won't double-announce alongside the removal note's role="status". */}
        <span
          className={`qr-logo-file__name${removePending ? ' qr-logo-file__name--removing' : ''}`}
          data-testid="logo-file-name"
          aria-live="polite"
        >
          {shown || 'No file chosen'}
        </span>
        {shown && (removePending ? (
          <span className="qr-logo-file__clear qr-logo-file__clear--inert" aria-hidden="true">✕</span>
        ) : (
          <button
            type="button"
            className="qr-logo-file__clear"
            data-testid="remove-logo"
            aria-label={pickedName ? 'Discard the selected file' : 'Remove logo'}
            ref={this.clearButtonRef}
            onClick={this.handleClear}
          >
            ✕
          </button>
        ))}
      </div>
    );
  }
}

LogoFileField.propTypes = {
  savedFilename: PropTypes.string,
  pickedName: PropTypes.string,
  removePending: PropTypes.bool,
  onPick: PropTypes.func,
  onClear: PropTypes.func,
};

LogoFileField.defaultProps = {
  savedFilename: null,
  pickedName: null,
  removePending: false,
  onPick: () => {},
  onClear: () => {},
};

export default LogoFileField;
